import React, { useEffect, useState } from 'react';
import { TagInputGq } from '../../../graphql/projects/mutations/tags/useEditTagsGq';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import InfoIcon from '../../../components/voxel/Icons/InfoIcon';
import VoxelTooltipShort from '../../../components/voxel/Tooltip/VoxelTooltipShort';
import VoxelAlert from '../../../components/voxel/Alert/VoxelAlert';

export interface AutotagTextInputProps {
  tagInput: TagInputGq;
  setTagInput: React.Dispatch<React.SetStateAction<TagInputGq>>;
  disabled?: boolean;
}

export const AutotagTextInput: React.FC<AutotagTextInputProps> = ({
  tagInput,
  setTagInput,
  disabled,
}) => {
  const [focused, setFocused] = useState(false);
  const [inputRef, setInputRef] = useState<HTMLTextAreaElement>(null);
  const [textValue, setTextValue] = useState(tagInput?.autoTagTags + ', ');
  const [tags, setTags] = useState<string[]>([]);
  useEffect(() => {
    const lastSeparatorPosition = Array.from(textValue.matchAll(/[\n,]+/gm)).reverse()[0]?.index;
    if (!!lastSeparatorPosition || lastSeparatorPosition == 0) {
      const newTags = textValue
        .slice(0, lastSeparatorPosition)
        .split(/[\n,]+/)
        .map((tv) => tv.trim().toLowerCase())
        .filter((v) => !!v.length)
        .map((tv) => tv.slice(0, 1).toUpperCase() + tv.slice(1, tv.length));

      setTags((prev) => [...prev, ...newTags]);
      setTextValue(textValue.slice(lastSeparatorPosition + 1, textValue.length));
    }
  }, [textValue]);

  const backspaceHandler = (event: React.KeyboardEvent) => {
    if (event.key === 'Backspace' && textValue.length === 0 && !!tags.length) {
      setTags((prev) => prev.slice(0, prev.length - 1));
    }
  };

  useEffect(() => {
    let cleanedTextValue = tags.join(', ');
    if (tags.length && textValue.length) {
      cleanedTextValue += ', ';
    }
    if (textValue.length) {
      cleanedTextValue += textValue;
    }
    if (tagInput?.autoTagTags !== cleanedTextValue) {
      setTagInput((prev) => ({ ...prev, autoTagTags: cleanedTextValue }));
    }
  }, [textValue, tags]);

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-row">
        <VoxelText variant="form-caption" className="flex-grow">
          Auto tag grammar variants
        </VoxelText>
        <VoxelTooltipShort
          message={
            'Used by the auto tag feature to consider multiple words as the same tag. E.g. "shoe" and "shoes"'
          }
          placement="bottom"
        >
          <InfoIcon size="1x" />
        </VoxelTooltipShort>
      </div>
      <div
        className={`
          w-full rounded
          px-3 py-3
          flex flex-row flex-wrap items-center content-start gap-2.5
          
          ${!focused && 'ring-1 ring-voxel-gray-200'}
          ${focused && 'ring-2 ring-primary-600'}
          focus:ring-2 focus:ring-primary-600
          active:ring-2 active:ring-primary-600
          min-h-[100px] max-h-[300px] overflow-y-scroll
    `}
        onClick={() => inputRef?.focus()}
      >
        {tags.map((tag, tagIndex) => (
          <div
            key={tagIndex}
            className="bg-voxel-blue-50/80 pl-2 pr-0.5 py-1 rounded-md text-voxel-blue-600 flex flex-row items-center gap-0.5"
          >
            {tag}
            <button
              disabled={disabled}
              type="button"
              className="rounded-md hover:bg-black/10  w-[17px] h-[17px] flex items-center justify-center opacity-30 hover:opacity-60 cursor-pointer"
              onClick={() => setTags((prev) => prev.filter((p, index) => index !== tagIndex))}
            >
              <FontAwesomeIcon icon={faTimes} fontSize={10} />
            </button>
          </div>
        ))}
        <textarea
          disabled={disabled}
          onPaste={() => {}}
          value={textValue}
          ref={setInputRef}
          onKeyDown={backspaceHandler}
          onChange={(event) => setTextValue(event.target.value)}
          className="outline-none flex-1 min-w-[100px] h-7 resize-none py-1"
          rows={1}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="Shoe, Shoes ..."
        />
      </div>
      <VoxelText variant="form-caption">Separate multiple variants with comma or enter</VoxelText>
      {!textValue?.length && !tags?.length && (
        <VoxelAlert size="xs" iconSize="sm">
          No variants detected - Required for automatic tagging
        </VoxelAlert>
      )}
    </div>
  );
};

export default AutotagTextInput;
